/**
 * Upload Service
 * Handles file uploads via presigned S3 URLs
 */

let API_BASE_URL = ""

async function loadApiBase(): Promise<string> {
  if (API_BASE_URL) {
    return API_BASE_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl = config.feedbackApiUrl
    if (!baseUrl) {
      throw new Error("API URL not found in aws-exports.json")
    }
    API_BASE_URL = baseUrl.replace(/\/?$/, "/")
    return API_BASE_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Upload API URL not configured")
  }
}

async function loadApiUrl(): Promise<string> {
  return `${await loadApiBase()}upload-url`
}

export interface UploadUrlResponse {
  uploadUrl: string
  fileKey: string
}

const ALLOWED_CONTENT_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
])

const ACCEPT_STRING = [
  ".xlsx", ".pptx", ".docx", ".pdf", ".csv",
  ".png", ".jpg", ".jpeg", ".gif", ".webp",
].join(",")

export function isAllowedFile(file: File): boolean {
  return ALLOWED_CONTENT_TYPES.has(file.type)
}

export function getAcceptString(): string {
  return ACCEPT_STRING
}

/**
 * Get a presigned URL for uploading a file to S3
 */
export async function getUploadUrl(
  filename: string,
  contentType: string,
  idToken: string
): Promise<UploadUrlResponse> {
  const apiUrl = await loadApiUrl()

  // macOS / iOS（特に iPhone/iPad からのアップロード）の filename は NFD（分解形）で
  // 来ることがある。S3 key を NFC（合成形）に揃えるため、ここでも正規化する。
  // サーバ側（upload-url Lambda）でも正規化しているが、defense in depth として
  // 送信前に揃えておく（後段で LLM が NFC キーを参照しても一致するように）。
  const normalizedFilename = filename.normalize("NFC")

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ filename: normalizedFilename, contentType }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Upload URL request failed: ${response.status}`)
  }

  return response.json()
}

// ── 生成ファイルの安定ダウンロード（2026-07-07）─────────────────────────
// チャットに埋め込まれた署名付き URL は約1時間で失効する。カード/画像はクリック・
// 表示エラー時にキーを取り出し、認証付き API でその場の新しい署名付き URL を得る。

/** 期限切れ（S3 の7日ライフサイクルでオブジェクト消滅）を UI が見分けるための例外。 */
export class DownloadExpiredError extends Error {}

/**
 * generated/ の S3 URL（署名付き・期限切れ問わず）から安定キーを取り出す。
 * amazonaws.com 以外・generated/ 以外は null（この機能の対象外）。
 */
export function s3KeyFromGeneratedUrl(url: string): string | null {
  try {
    // OCI: 同一オリジンプロキシは相対 /os/<bucket>/generated/… で来る。base を与えて
    // パースし、amazonaws（AWS）と /os（OCI）の双方から generated/ 以降のキーを取る。
    const u = new URL(url, "https://placeholder.invalid")
    const isAws = /(^|\.)amazonaws\.com$/.test(u.hostname)
    const isOci = u.hostname === "placeholder.invalid" && u.pathname.startsWith("/os/")
    if (!isAws && !isOci) return null
    const path = decodeURIComponent(u.pathname)
    const idx = path.indexOf("/generated/")
    if (idx === -1) return null
    return path.slice(idx + 1)
  } catch {
    return null
  }
}

/**
 * generated/ オブジェクトの新しい署名付き GET URL をその場で取得する。
 * 404（保存期間7日超過）は DownloadExpiredError を投げる。
 *
 * disposition（2026-07-16・HTML 成果物のブラウザ表示）:
 *  - "inline": ブラウザ表示用（.html は Content-Type: text/html を明示した署名になる）
 *  - "attachment": 強制ダウンロード用（.html がタブ表示になるのを防ぐ）
 *  - 省略時: 従来どおり素の署名
 */
export async function getFreshDownloadUrl(
  fileKey: string,
  idToken: string,
  disposition?: "inline" | "attachment"
): Promise<string> {
  const apiUrl = `${await loadApiBase()}download-url`
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(disposition ? { fileKey, disposition } : { fileKey }),
  })
  if (response.status === 404) {
    throw new DownloadExpiredError(
      "ファイルの保存期間（7日）を過ぎたため取得できません。"
    )
  }
  if (!response.ok) {
    throw new Error(`Download URL request failed: ${response.status}`)
  }
  const data = await response.json()
  return data.downloadUrl
}

/**
 * Upload a file directly to S3 using a presigned URL
 */
export async function uploadFileToS3(file: File, uploadUrl: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`)
  }
}
